/**
 * GameState — module-singleton holding cross-scene run state.
 *
 * Phaser destroys and recreates Scene instances on every `scene.start()`,
 * so per-scene fields cannot carry run progress. This singleton is the
 * durable home for "which hero, which stage, how many lives" between scenes.
 *
 * Kept framework-free (no Phaser import) so it is trivially unit-testable.
 */

import { getHeroSummonConfig, SummonConfig } from '../data/backupSummons';
import { scoreAttackStageMultiplier } from '@/systems/BossRushSequence';

export type Difficulty = 'pint' | 'pub' | 'brawl' | 'manor';

export type GameMode = 'campaign' | 'boss-rush' | 'score-attack' | 'survival';

export interface RunState {
  /** Selected hero id (see src/data/heroes.ts). Null until CharSelect. */
  heroId: string | null;
  /** Selected P2 hero id (co-op only). Null until P2 confirms in CharSelect. */
  p2HeroId: string | null;
  /** 1-indexed current stage (1..9). */
  stage: number;
  /** Running score for the current run. */
  score: number;
  /** Remaining lives (shared pool for both players). */
  lives: number;
  /** Chosen difficulty. */
  difficulty: Difficulty;
  /** Whether the player won the most recent stage (drives Results copy). */
  lastStageCleared: boolean;
  /** Whether co-op mode was selected from the title screen. */
  coopMode: boolean;
  /** Whether players can damage each other. Default false. */
  friendlyFire: boolean;
  /** How many times the player has accepted Halloway's deal (0, 1, or 2 = sell-out). */
  tookDeal: number;
  /**
   * 0-indexed section to start from on death/continue for the current stage.
   * Advances each time a section clears; resets to 0 when a stage is cleared.
   */
  checkpointSection: number;
  /** Active game mode for the current run. */
  gameMode: GameMode;
  /**
   * Boss Rush only: 0-indexed position in the BOSS_RUSH_SEQUENCE.
   * 0 = first boss, 8 = last boss. Unused in campaign/score-attack/survival.
   */
  bossRushIndex: number;
  /**
   * Survival only: current 1-indexed wave number.
   * Incremented on each wave clear; reset to 1 on newRun().
   */
  survivalWave: number;
  /**
   * Score thresholds (multiples of SCORE_1UP_INTERVAL) already rewarded with
   * an extra life this run. Prevents double-awarding on repeated addScore calls
   * that straddle the same threshold.
   */
  awardedScoreThresholds: number[];
  /**
   * True when all evidence collectibles have been gathered this run.
   * Gates the 'Follow the Money' ending (#14) together with Hard difficulty.
   */
  evidenceCollected: boolean;
  /**
   * Extra lives earned since the last call to consumeExtraLives().
   * StageScene reads this each frame to trigger HUD flash + SFX, then resets it.
   */
  pendingExtraLives: number;
}

const STARTING_LIVES = 3;
const FINAL_STAGE = 9;
/** Score interval at which a free life is awarded (classic arcade extend). */
export const SCORE_1UP_INTERVAL = 50_000;

function freshRun(): RunState {
  return {
    heroId: null,
    p2HeroId: null,
    stage: 1,
    score: 0,
    lives: STARTING_LIVES,
    difficulty: 'pub',
    lastStageCleared: false,
    coopMode: false,
    friendlyFire: false,
    tookDeal: 0,
    checkpointSection: 0,
    gameMode: 'campaign',
    bossRushIndex: 0,
    survivalWave: 1,
    awardedScoreThresholds: [],
    pendingExtraLives: 0,
    evidenceCollected: false,
  };
}

class GameStateController {
  private state: RunState = freshRun();

  /** Reset everything for a brand-new run (called from Title → CharSelect). */
  newRun(): void {
    this.state = freshRun();
  }

  get current(): Readonly<RunState> {
    return this.state;
  }

  selectHero(heroId: string): void {
    this.state.heroId = heroId;
  }

  selectP2Hero(heroId: string): void {
    this.state.p2HeroId = heroId;
  }

  setFriendlyFire(enabled: boolean): void {
    this.state.friendlyFire = enabled;
  }

  setDifficulty(difficulty: Difficulty): void {
    this.state.difficulty = difficulty;
  }

  enableCoop(): void {
    this.state.coopMode = true;
  }

  addScore(points: number): void {
    // Score Attack scales every point by the current stage's multiplier
    // (later stages are worth more — the mode's whole hook). Other modes = ×1.
    const mult =
      this.state.gameMode === 'score-attack' ? scoreAttackStageMultiplier(this.state.stage) : 1;
    this.state.score += Math.max(0, Math.floor(points * mult));
    // Award a 1-up for each newly crossed SCORE_1UP_INTERVAL threshold.
    const lastAwarded =
      this.state.awardedScoreThresholds.length > 0
        ? this.state.awardedScoreThresholds[this.state.awardedScoreThresholds.length - 1]
        : 0;
    const maxThreshold = Math.floor(this.state.score / SCORE_1UP_INTERVAL) * SCORE_1UP_INTERVAL;
    for (let t = lastAwarded + SCORE_1UP_INTERVAL; t <= maxThreshold; t += SCORE_1UP_INTERVAL) {
      this.state.awardedScoreThresholds.push(t);
      this.state.lives += 1;
      this.state.pendingExtraLives += 1;
    }
  }

  loseLife(): void {
    this.state.lives = Math.max(0, this.state.lives - 1);
  }

  /** Award a free life directly (e.g. item pickup). */
  gainLife(): void {
    this.state.lives += 1;
  }

  /**
   * Consume and return the count of extra lives pending HUD/SFX feedback.
   * Call once per frame from StageScene; resets to 0 after reading.
   */
  consumeExtraLives(): number {
    const n = this.state.pendingExtraLives;
    this.state.pendingExtraLives = 0;
    return n;
  }

  get isGameOver(): boolean {
    return this.state.lives <= 0;
  }

  /** Mark current stage cleared; advance pointer if more stages remain. */
  clearStage(): void {
    this.state.lastStageCleared = true;
    this.state.checkpointSection = 0; // reset for the next stage
    if (this.state.stage < FINAL_STAGE) {
      this.state.stage += 1;
    }
  }

  /** Record the section index to resume from on death or continue. */
  setCheckpoint(section: number): void {
    this.state.checkpointSection = Math.max(0, section);
  }

  get isFinalStageCleared(): boolean {
    return this.state.stage >= FINAL_STAGE && this.state.lastStageCleared;
  }

  /** Set stage when resuming from a save (Title → Continue). Clamps to [1, FINAL_STAGE]. */
  resumeAtStage(stage: number): void {
    this.state.stage = Math.max(1, Math.min(FINAL_STAGE, stage));
  }

  /** Record that the player accepted Halloway's deal once more. */
  incrementTookDeal(): void {
    this.state.tookDeal += 1;
  }

  /** Mark all evidence collectibles as gathered this run (gates ending #14). */
  collectAllEvidence(): void {
    this.state.evidenceCollected = true;
  }

  setGameMode(mode: GameMode): void {
    this.state.gameMode = mode;
  }

  /** Set the life pool directly (e.g. mode entry: Boss Rush uses BOSS_RUSH_LIVES). */
  setLives(n: number): void {
    this.state.lives = Math.max(0, Math.floor(n));
  }

  /** Advance boss-rush index to the next boss. */
  advanceBossRush(): void {
    this.state.bossRushIndex += 1;
  }

  /** Advance survival wave counter on each wave clear. */
  advanceSurvivalWave(): void {
    this.state.survivalWave += 1;
  }

  /**
   * Return the Backup Meter summon configuration for the active P1 hero.
   * Falls back to the default K9 Dog Squad config when no hero is selected.
   */
  get activeSummonConfig(): SummonConfig {
    return getHeroSummonConfig(this.state.heroId);
  }
}

/** Shared singleton instance — import this everywhere. */
export const GameState = new GameStateController();
export { FINAL_STAGE, STARTING_LIVES };
